import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { isStandaloneDisplayMode } from '../utils/pwaDisplayMode';

/**
 * Optional "Install the app" control for browsers that fire beforeinstallprompt
 * (Chrome / Edge on Android and desktop).
 *
 * Hidden when:
 * - the app is already in standalone / iOS home-screen mode
 * - the browser never offers an install prompt (Safari iOS, Firefox, etc.)
 * - the user already installed the app (appinstalled)
 *
 * Keep this component mounted (do not create it only when a menu opens),
 * otherwise beforeinstallprompt can be missed.
 */
export default function InstallPWA({ className = '', onPrompt }) {
  const { t } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandaloneDisplayMode());

  useEffect(() => {
    if (isStandaloneDisplayMode()) {
      setInstalled(true);
      return undefined;
    }

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (installed || !deferredPrompt) return null;

  const handleInstallClick = async () => {
    const promptEvent = deferredPrompt;
    if (!promptEvent) return;

    onPrompt?.();
    promptEvent.prompt();

    try {
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
      }
    } catch (error) {
      console.warn('PWA install prompt failed', error);
    } finally {
      setDeferredPrompt(null);
    }
  };

  return (
    <button
      type="button"
      onClick={handleInstallClick}
      className={
        className ||
        'w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700'
      }
    >
      {t('pwa.install')}
    </button>
  );
}
